import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Select
} from '@material-ui/core';
import Search from '@material-ui/icons/SearchOutlined';
import Grid from "@material-ui/core/Grid";
import LabelValue from "../../../shared/labelvalue/LabelValue.js";
import useFormularioInscricao from './useFormularioInscricao';
import * as Yup from "yup";
import {
    CelularNumberFormatCustom,
    CepNumberFormatCustom,
    CpfNumberFormatCustom,
    DataNascimentoNumberFormatCustom,
    RgNumberFormatCustom
} from '../../../shared/formatters';
import { URL_ESTADO_CIVIL, URL_RACA, URL_NACIONALIDADE, URL_ENDERECO_CEPS } from "../../../../constants";
import useAlertMessage from "../../../../hooks/useAlertMessage";
import DialogBuscarEndereco from './DialogBuscarEndereco.jsx';
import useFetch from "../../../../hooks/useFetch.js";
import GenericTab from '../../../shared/generictab/GenericTab.js';
import { containerPaper, tituloSecao } from "../../../../utils/styles.js";
import { useContextoResumoAcoes } from './ContextoResumoInscricao.jsx';
import { buildTabContent, calcularIdade as calculateAge, getStepSections, STEP_IDS } from './inscricaoSections';

const useStyles = makeStyles(theme => ({
    /* Campo do nome social com o "Habilitar" ao lado, na mesma linha. */
    linhaNomeSocial: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    campoNomeSocial: {
        flex: 1
    },
    habilitarNomeSocial: {
        marginRight: 0
    },
    ...containerPaper(theme),
    root: {
        marginBottom: theme.spacing(2)
    },
    tabs: {
        width: '100%',
        minWidth: '100px',
        maxWidth: '160px',
        marginRight: theme.spacing(0),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '100px',
                maxWidth: '160px',
            }
        }
    },
    textfield: {
        backgroundColor: 'white',
        '& > div:focus': {
            backgroundColor: 'white'
        }
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    helperText: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        marginBottom: theme.spacing(1)
    }
}));

/* Campo com máscara digitado pela metade ainda tem o '_' da máscara. */
const MENSAGEM_INCOMPLETO = 'Preencha o campo completo';
const completo = (valor) => !valor || !String(valor).includes('_');

export const schemaDadosPessoais = Yup.object().shape({
    pessoa: Yup.object().shape({
        nome: Yup.string().required('Nome é obrigatório'),
        nomeSocial: Yup.string().nullable(),
        emailAtual: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
        telefone: Yup.string().nullable(),
        celular: Yup.string().required('Celular é obrigatório').test('completo', MENSAGEM_INCOMPLETO, completo),
        celularDois: Yup.string().nullable().test('completo', MENSAGEM_INCOMPLETO, completo),
        sexo: Yup.string().required('Sexo é obrigatório'),
        estadoCivil: Yup.string().required('Estado civil é obrigatório'),
        raca: Yup.string().required('Raça é obrigatória'),
        nacionalidade: Yup.string().required('Nacionalidade é obrigatória'),
        nomeMae: Yup.string().required('Nome da mãe é obrigatório'),
        nomePai: Yup.string().nullable(),
        rg: Yup.string().required('RG é obrigatório').test('completo', MENSAGEM_INCOMPLETO, completo),
        orgaoRg: Yup.string().required('Órgão expedidor/UF é obrigatório'),
    }),
    endereco: Yup.object().shape({
        cep: Yup.string().required('CEP é obrigatório').test('completo', MENSAGEM_INCOMPLETO, completo),
        logradouro: Yup.string().required('Logradouro é obrigatório'),
        numero: Yup.string().required('Número é obrigatório'),
        complemento: Yup.string().nullable(),
        bairro: Yup.string().required('Bairro é obrigatório'),
        cidade: Yup.object().shape({
            nome: Yup.string().required('Cidade é obrigatória'),
            uf: Yup.object().shape({
                uf: Yup.string().required('Estado (UF) é obrigatório')
            }).required()
        }).required()
    }),
    // Condicionais baseados em menor de idade
    tipoResponsavelLegal: Yup.string().when('pessoa.dataNascimento', {
        is: (val) => calculateAge(val) < 18,
        then: Yup.string().required('Tipo de responsável legal é obrigatório'),
        otherwise: Yup.string().nullable()
    }),
    dataEmancipacao: Yup.string().when('tipoResponsavelLegal', {
        is: 'EMANCIPADO',
        then: Yup.string().required('Data de emancipação é obrigatória').test('completo', MENSAGEM_INCOMPLETO, completo),
        otherwise: Yup.string().nullable()
    }),
    responsavelLegal: Yup.object().when(['tipoResponsavelLegal', 'pessoa.dataNascimento'], {
        is: (tipoResponsavelLegal, dataNascimento) => tipoResponsavelLegal && tipoResponsavelLegal !== 'EMANCIPADO' && calculateAge(dataNascimento) < 18,
        then: Yup.object().shape({
            nome: Yup.string().required('Nome do responsável é obrigatório'),
            cpf: Yup.string().required('CPF do responsável é obrigatório').test('completo', MENSAGEM_INCOMPLETO, completo),
            rg: Yup.string().required('RG do responsável é obrigatório').test('completo', MENSAGEM_INCOMPLETO, completo),
            dataNascimento: Yup.string().required('Data de nascimento é obrigatória').test('completo', MENSAGEM_INCOMPLETO, completo),
            emailAtual: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
            celular: Yup.string().required('Celular é obrigatório').test('completo', MENSAGEM_INCOMPLETO, completo),
            endereco: Yup.object().shape({
                cep: Yup.string().required('CEP é obrigatório').test('completo', MENSAGEM_INCOMPLETO, completo),
                logradouro: Yup.string().required('Logradouro é obrigatório'),
                numero: Yup.string().required('Número é obrigatório'),
                bairro: Yup.string().required('Bairro é obrigatório'),
                cidade: Yup.object().shape({
                    nome: Yup.string().required('Cidade é obrigatória'),
                    uf: Yup.object().shape({
                        uf: Yup.string().required('Estado (UF) é obrigatório')
                    }).required()
                }).required()
            })
        }),
        otherwise: Yup.object().nullable()
    })
});

const FormDadosPessoais = forwardRef((props, ref) => {
    const classes = useStyles();
    const tabsRef = useRef(null);
    const fetch = useFetch();
    const [fetchSilent] = useFetch({ returnInnerLoadingState: true });
    /* somenteConsulta: fora do período de inscrição a etapa abre só para consulta, nada pode ser alterado. */
    const { data, onUpdate, isEdit, identificacao, formulario = {}, activeSectionId, somenteConsulta = false } = props;
    /*
     * Nome da mãe: bloqueado na edição (como já era) e também na inscrição
     * nova quando a pessoa já é cadastrada (achada pelo CPF na identificação).
     * Sem isso, uma edição legítima do campo levaria ao novo 403 do back
     * (nome da mãe diferente do cadastro no ADGERAL).
     */
    const nomeMaeBloqueado = isEdit || !!identificacao?.dadosCadastrais?.pessoa?.id;
    const [habilitarNomeSocial, setHabilitarNomeSocial] = useState(!!(data?.pessoa?.nomeSocial));
    const showAlertMessage = useAlertMessage();
    const setDadosResumo = useContextoResumoAcoes();
    const [buscarEnderecoOpen, setBuscarEnderecoOpen] = useState(false);
    const [addressTargetType, setAddressTargetType] = useState('candidato');
    const [estadosCivis, setEstadosCivis] = useState([]);
    const [racas, setRacas] = useState([]);
    const [nacionalidades, setNacionalidades] = useState([]);

    const formEtapa = useFormularioInscricao({
        valoresIniciais: { ...data },
        reinicializar: true,
        schema: schemaDadosPessoais
    });

    useImperativeHandle(ref, () => ({
        formulario: formEtapa.api
    }));

    useEffect(() => {
        if (setDadosResumo) {
            setDadosResumo(formEtapa.valores);
        }
    }, [formEtapa.valores, setDadosResumo]);

    useEffect(() => {
        if (formEtapa.submitCount > 0 && !formEtapa.valido) {
            const firstErrorIdx = tabContent.findIndex(item => item.hasError);
            if (firstErrorIdx !== -1) {
                tabsRef.current?.setTabIndex(firstErrorIdx);
            }
        }
    }, [formEtapa.submitCount, formEtapa.valido]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [resEstadosCivis, resRacas, resNacionalidades] = await Promise.all([
                    fetch(URL_ESTADO_CIVIL, null, null, null, false),
                    fetch(URL_RACA, null, null, null, false),
                    fetch(URL_NACIONALIDADE, null, null, null, true)
                ]);
                setEstadosCivis(resEstadosCivis || []);
                setRacas(resRacas || []);
                setNacionalidades(resNacionalidades || []);
            } catch (error) {
                // O useFetch já avisa o usuário da falha; as listas ficam vazias.
            }
        };
        fetchData();
    }, []);

    // Nacionalidade já vem como Brasileira enquanto não houver outra escolhida.
    useEffect(() => {
        if (somenteConsulta || formEtapa.valores.pessoa?.nacionalidade) return;
        const brasileira = nacionalidades.find(n => String(n.descricao || '').trim().toLowerCase() === 'brasileira');
        if (brasileira) formEtapa.definirValor('pessoa.nacionalidade', brasileira.id);
    }, [nacionalidades, formEtapa.valores.pessoa?.nacionalidade, somenteConsulta]);

    const handleCepBlur = async (e, type = 'candidato') => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length === 8) {
            fetchSilent(
                `${URL_ENDERECO_CEPS}/${cep}`,
                null,
                (data) => {
                    if (data) {
                        const mappedCidade = data.cidade || {
                            nome: data.cidadeNome || '',
                            uf: { uf: '' }
                        };
                        if (type === 'candidato') {
                            formEtapa.definirValor('endereco.logradouro', data.logradouro || '');
                            formEtapa.definirValor('endereco.bairro', data.bairro || '');
                            formEtapa.definirValor('endereco.cidade', mappedCidade);
                        } else {
                            formEtapa.definirValor('responsavelLegal.endereco.logradouro', data.logradouro || '');
                            formEtapa.definirValor('responsavelLegal.endereco.bairro', data.bairro || '');
                            formEtapa.definirValor('responsavelLegal.endereco.cidade', mappedCidade);
                        }
                    }
                },
                (error, tratarErro) => {
                    tratarErro(error);
                    const emptyCidade = { nome: '', uf: { uf: '' } };
                    if (type === 'candidato') {
                        formEtapa.definirValor('endereco.logradouro', '');
                        formEtapa.definirValor('endereco.bairro', '');
                        formEtapa.definirValor('endereco.cidade', emptyCidade);
                    } else {
                        formEtapa.definirValor('responsavelLegal.endereco.logradouro', '');
                        formEtapa.definirValor('responsavelLegal.endereco.bairro', '');
                        formEtapa.definirValor('responsavelLegal.endereco.cidade', emptyCidade);
                    }
                    showAlertMessage("O CEP informado não consta na base de dados da instituição. Você pode pesquisar usando o botão de busca ou digitar os campos manualmente.");
                }
            );
        }
    };

    const handleHabilitarNomeSocialChange = (e) => {
        const checked = e.target.checked;
        setHabilitarNomeSocial(checked);
        if (checked) {
            showAlertMessage("Este campo é destinado ao nome pelo qual você prefere ser identificado(a), respeitando sua identidade de gênero. O uso do nome social é garantido pelo Decreto nº 8.727/2016 e outras legislações brasileiras, assegurando que sua identidade seja respeitada em todos os registros e cadastros. Caso você seja uma pessoa transgênero, travesti ou não-binária, utilize este campo para inserir o nome pelo qual deseja ser tratado(a), independentemente do nome registrado em seus documentos oficiais. O preenchimento do nome social é opcional.");
        }
    };

    const sections = getStepSections(STEP_IDS.DADOS_PESSOAIS, { formulario, inscricao: formEtapa.valores });

    const tabEntries = [
        {
            id: 'dadosBasicos',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Dados Pessoais</h4>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label='Nome Completo *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.nome && !!formEtapa.erros.pessoa?.nome}>
                                <OutlinedInput
                                    name="pessoa.nome"
                                    value={formEtapa.valores.pessoa.nome}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                    disabled={isEdit}
                                />
                                {formEtapa.tocados.pessoa?.nome &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.nome}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label='Nome Social'>
                            <div className={classes.linhaNomeSocial}>
                                <FormControl variant="outlined" disabled={somenteConsulta} className={classes.campoNomeSocial}>
                                    <OutlinedInput
                                        name="pessoa.nomeSocial"
                                        value={formEtapa.valores.pessoa.nomeSocial || ''}
                                        onChange={formEtapa.aoMudar}
                                        disabled={somenteConsulta || !habilitarNomeSocial}
                                    />
                                </FormControl>
                                <FormControlLabel
                                    className={classes.habilitarNomeSocial}
                                    disabled={somenteConsulta}
                                    label="Habilitar"
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={habilitarNomeSocial}
                                            onChange={handleHabilitarNomeSocialChange}
                                            color="primary"
                                        />
                                    }
                                />
                            </div>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='E-mail *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.emailAtual && !!formEtapa.erros.pessoa?.emailAtual}>
                                <OutlinedInput
                                    name="pessoa.emailAtual"
                                    value={formEtapa.valores.pessoa.emailAtual}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                />
                                {formEtapa.tocados.pessoa?.emailAtual &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.emailAtual}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Celular *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.celular && !!formEtapa.erros.pessoa?.celular}>
                                <OutlinedInput
                                    name="pessoa.celular"
                                    inputComponent={CelularNumberFormatCustom}
                                    value={String(formEtapa.valores.pessoa.celular ?? '')}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                    placeholder="(00) 00000-0000"
                                />
                                {formEtapa.tocados.pessoa?.celular &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.celular}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Telefone Secundário'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.celularDois && !!formEtapa.erros.pessoa?.celularDois}>
                                <OutlinedInput
                                    name="pessoa.celularDois"
                                    inputComponent={CelularNumberFormatCustom}
                                    value={String(formEtapa.valores.pessoa.celularDois ?? '')}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                    placeholder="(00) 00000-0000"
                                />
                                {formEtapa.tocados.pessoa?.celularDois &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.celularDois}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Sexo *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.sexo && !!formEtapa.erros.pessoa?.sexo}>
                                <Select
                                    name="pessoa.sexo"
                                    value={formEtapa.valores.pessoa.sexo}
                                    onChange={formEtapa.aoMudar}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    <MenuItem value="MASCULINO">Masculino</MenuItem>
                                    <MenuItem value="FEMININO">Feminino</MenuItem>
                                </Select>
                                {formEtapa.tocados.pessoa?.sexo &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.sexo}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Estado Civil *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.estadoCivil && !!formEtapa.erros.pessoa?.estadoCivil}>
                                <Select
                                    name="pessoa.estadoCivil"
                                    value={formEtapa.valores.pessoa.estadoCivil}
                                    onChange={formEtapa.aoMudar}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {estadosCivis.map(ec => (
                                        <MenuItem key={ec.id} value={ec.id}>{ec.descricao}</MenuItem>
                                    ))}
                                </Select>
                                {formEtapa.tocados.pessoa?.estadoCivil &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.estadoCivil}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Raça *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.raca && !!formEtapa.erros.pessoa?.raca}>
                                <Select
                                    name="pessoa.raca"
                                    value={formEtapa.valores.pessoa.raca}
                                    onChange={formEtapa.aoMudar}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {racas.map(r => (
                                        <MenuItem key={r.id} value={r.id}>{r.descricao}</MenuItem>
                                    ))}
                                </Select>
                                {formEtapa.tocados.pessoa?.raca &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.raca}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Nacionalidade *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.nacionalidade && !!formEtapa.erros.pessoa?.nacionalidade}>
                                <Select
                                    name="pessoa.nacionalidade"
                                    value={formEtapa.valores.pessoa.nacionalidade}
                                    onChange={formEtapa.aoMudar}
                                >
                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                    {nacionalidades.map(n => (
                                        <MenuItem key={n.id} value={n.id}>{n.descricao}</MenuItem>
                                    ))}
                                </Select>
                                {formEtapa.tocados.pessoa?.nacionalidade &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.nacionalidade}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Nome da Mãe *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.nomeMae && !!formEtapa.erros.pessoa?.nomeMae}>
                                <OutlinedInput
                                    name="pessoa.nomeMae"
                                    value={formEtapa.valores.pessoa.nomeMae}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                    disabled={nomeMaeBloqueado}
                                />
                                {formEtapa.tocados.pessoa?.nomeMae &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.nomeMae}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Nome do Pai'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.nomePai && !!formEtapa.erros.pessoa?.nomePai}>
                                <OutlinedInput
                                    name="pessoa.nomePai"
                                    value={formEtapa.valores.pessoa.nomePai || ''}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                />
                                {formEtapa.tocados.pessoa?.nomePai &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.nomePai}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label='Data de Nascimento'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth>
                                <OutlinedInput
                                    name="pessoa.dataNascimento"
                                    value={formEtapa.valores.pessoa.dataNascimento || ''}
                                    disabled
                                />
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <LabelValue label='RG *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.rg && !!formEtapa.erros.pessoa?.rg}>
                                <OutlinedInput
                                    name="pessoa.rg"
                                    inputComponent={RgNumberFormatCustom}
                                    value={String(formEtapa.valores.pessoa.rg ?? '')}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                />
                                {formEtapa.tocados.pessoa?.rg &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.rg}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <LabelValue label='Órgão Expedidor/UF *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.pessoa?.orgaoRg && !!formEtapa.erros.pessoa?.orgaoRg}>
                                <OutlinedInput
                                    name="pessoa.orgaoRg"
                                    value={formEtapa.valores.pessoa.orgaoRg}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                    placeholder="SSP/SP"
                                />
                                {formEtapa.tocados.pessoa?.orgaoRg &&
                                    <FormHelperText>{formEtapa.erros.pessoa?.orgaoRg}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                </Grid>
            )
        },
        {
            id: 'endereco',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Endereço Residencial</h4>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='CEP *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.endereco?.cep && !!formEtapa.erros.endereco?.cep}>
                                <OutlinedInput
                                    name="endereco.cep"
                                    inputComponent={CepNumberFormatCustom}
                                    value={String(formEtapa.valores.endereco.cep ?? '')}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={(e) => {
                                        formEtapa.aoSair(e);
                                        handleCepBlur(e, 'candidato');
                                    }}
                                    placeholder="00000-000"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="buscar endereco candidato"
                                                disabled={somenteConsulta}
                                                onClick={() => {
                                                    setAddressTargetType('candidato');
                                                    setBuscarEnderecoOpen(true);
                                                }}
                                                edge="end"
                                            >
                                                <Search />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                {formEtapa.tocados.endereco?.cep &&
                                    <FormHelperText>{formEtapa.erros.endereco?.cep}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <LabelValue label='Logradouro *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.endereco?.logradouro && !!formEtapa.erros.endereco?.logradouro}>
                                <OutlinedInput
                                    name="endereco.logradouro"
                                    value={formEtapa.valores.endereco.logradouro}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                />
                                {formEtapa.tocados.endereco?.logradouro &&
                                    <FormHelperText>{formEtapa.erros.endereco?.logradouro}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Número *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.endereco?.numero && !!formEtapa.erros.endereco?.numero}>
                                <OutlinedInput
                                    name="endereco.numero"
                                    value={formEtapa.valores.endereco.numero}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                />
                                {formEtapa.tocados.endereco?.numero &&
                                    <FormHelperText>{formEtapa.erros.endereco?.numero}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <LabelValue label='Complemento'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth>
                                <OutlinedInput
                                    name="endereco.complemento"
                                    value={formEtapa.valores.endereco.complemento || ''}
                                    onChange={formEtapa.aoMudar}
                                />
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <LabelValue label='Bairro *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.endereco?.bairro && !!formEtapa.erros.endereco?.bairro}>
                                <OutlinedInput
                                    name="endereco.bairro"
                                    value={formEtapa.valores.endereco.bairro}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                />
                                {formEtapa.tocados.endereco?.bairro &&
                                    <FormHelperText>{formEtapa.erros.endereco?.bairro}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <LabelValue label='Cidade *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.endereco?.cidade?.nome && !!formEtapa.erros.endereco?.cidade?.nome}>
                                <OutlinedInput
                                    name="endereco.cidade.nome"
                                    value={formEtapa.valores.endereco.cidade?.nome || ''}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                />
                                {formEtapa.tocados.endereco?.cidade?.nome &&
                                    <FormHelperText>{formEtapa.erros.endereco?.cidade?.nome}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <LabelValue label='UF *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.endereco?.cidade?.uf?.uf && !!formEtapa.erros.endereco?.cidade?.uf?.uf}>
                                <OutlinedInput
                                    name="endereco.cidade.uf.uf"
                                    value={formEtapa.valores.endereco.cidade?.uf?.uf || ''}
                                    onChange={formEtapa.aoMudar}
                                    onBlur={formEtapa.aoSair}
                                    placeholder="SP"
                                />
                                {formEtapa.tocados.endereco?.cidade?.uf?.uf &&
                                    <FormHelperText>{formEtapa.erros.endereco?.cidade?.uf?.uf}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>
                </Grid>
            )
        },
        {
            id: 'responsavelLegal',
            content: (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h4 className={classes.sectionTitle}>Responsável Legal</h4>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label='Tipo de Responsável Legal *'>
                            <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                error={formEtapa.tocados.tipoResponsavelLegal && !!formEtapa.erros.tipoResponsavelLegal}>
                                <Select
                                    name="tipoResponsavelLegal"
                                    value={formEtapa.valores.tipoResponsavelLegal}
                                    onChange={formEtapa.aoMudar}
                                >
                                    <MenuItem value="PAI">Pai</MenuItem>
                                    <MenuItem value="MAE">Mãe</MenuItem>
                                    <MenuItem value="TUTOR">Tutor Legal</MenuItem>
                                    <MenuItem value="EMANCIPADO">Estudante Emancipado</MenuItem>
                                </Select>
                                {formEtapa.tocados.tipoResponsavelLegal &&
                                    <FormHelperText>{formEtapa.erros.tipoResponsavelLegal}</FormHelperText>}
                            </FormControl>
                        </LabelValue>
                    </Grid>

                    {formEtapa.valores.tipoResponsavelLegal === 'EMANCIPADO' && (
                        <Grid item xs={12} sm={6}>
                            <LabelValue label='Data de Emancipação *'>
                                <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                    error={formEtapa.tocados.dataEmancipacao && !!formEtapa.erros.dataEmancipacao}>
                                    <OutlinedInput
                                        name="dataEmancipacao"
                                        inputComponent={DataNascimentoNumberFormatCustom}
                                        value={String(formEtapa.valores.dataEmancipacao ?? '')}
                                        onChange={formEtapa.aoMudar}
                                        onBlur={formEtapa.aoSair}
                                        placeholder="DD/MM/AAAA"
                                    />
                                    {formEtapa.tocados.dataEmancipacao &&
                                        <FormHelperText>{formEtapa.erros.dataEmancipacao}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    )}

                    {formEtapa.valores.tipoResponsavelLegal && formEtapa.valores.tipoResponsavelLegal !== 'EMANCIPADO' && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label='Nome do Responsável *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.nome && !!formEtapa.erros.responsavelLegal?.nome}>
                                        <OutlinedInput
                                            name="responsavelLegal.nome"
                                            value={formEtapa.valores.responsavelLegal?.nome || ''}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.responsavelLegal?.nome &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.nome}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='CPF do Responsável *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.cpf && !!formEtapa.erros.responsavelLegal?.cpf}>
                                        <OutlinedInput
                                            name="responsavelLegal.cpf"
                                            inputComponent={CpfNumberFormatCustom}
                                            value={String(formEtapa.valores.responsavelLegal?.cpf ?? '')}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                            placeholder="000.000.000-00"
                                        />
                                        {formEtapa.tocados.responsavelLegal?.cpf &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.cpf}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='RG do Responsável *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.rg && !!formEtapa.erros.responsavelLegal?.rg}>
                                        <OutlinedInput
                                            name="responsavelLegal.rg"
                                            inputComponent={RgNumberFormatCustom}
                                            value={String(formEtapa.valores.responsavelLegal?.rg ?? '')}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.responsavelLegal?.rg &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.rg}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='Data de Nascimento do Responsável *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.dataNascimento && !!formEtapa.erros.responsavelLegal?.dataNascimento}>
                                        <OutlinedInput
                                            name="responsavelLegal.dataNascimento"
                                            inputComponent={DataNascimentoNumberFormatCustom}
                                            value={String(formEtapa.valores.responsavelLegal?.dataNascimento ?? '')}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                            placeholder="DD/MM/AAAA"
                                        />
                                        {formEtapa.tocados.responsavelLegal?.dataNascimento &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.dataNascimento}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label='E-mail do Responsável *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.emailAtual && !!formEtapa.erros.responsavelLegal?.emailAtual}>
                                        <OutlinedInput
                                            name="responsavelLegal.emailAtual"
                                            value={formEtapa.valores.responsavelLegal?.emailAtual || ''}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.responsavelLegal?.emailAtual &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.emailAtual}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label='Celular do Responsável *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.celular && !!formEtapa.erros.responsavelLegal?.celular}>
                                        <OutlinedInput
                                            name="responsavelLegal.celular"
                                            inputComponent={CelularNumberFormatCustom}
                                            value={String(formEtapa.valores.responsavelLegal?.celular ?? '')}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                            placeholder="(00) 00000-0000"
                                        />
                                        {formEtapa.tocados.responsavelLegal?.celular &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.celular}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>

                            <Grid item xs={12}>
                                <h4 className={classes.sectionTitle}>Endereço do Responsável Legal</h4>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='CEP *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.endereco?.cep && !!formEtapa.erros.responsavelLegal?.endereco?.cep}>
                                        <OutlinedInput
                                            name="responsavelLegal.endereco.cep"
                                            inputComponent={CepNumberFormatCustom}
                                            value={String(formEtapa.valores.responsavelLegal?.endereco?.cep ?? '')}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={(e) => {
                                                formEtapa.aoSair(e);
                                                handleCepBlur(e, 'responsavel');
                                            }}
                                            placeholder="00000-000"
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="buscar endereco responsavel"
                                                        disabled={somenteConsulta}
                                                        onClick={() => {
                                                            setAddressTargetType('responsavel');
                                                            setBuscarEnderecoOpen(true);
                                                        }}
                                                        edge="end"
                                                    >
                                                        <Search />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                        {formEtapa.tocados.responsavelLegal?.endereco?.cep &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.endereco?.cep}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <LabelValue label='Logradouro *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.endereco?.logradouro && !!formEtapa.erros.responsavelLegal?.endereco?.logradouro}>
                                        <OutlinedInput
                                            name="responsavelLegal.endereco.logradouro"
                                            value={formEtapa.valores.responsavelLegal?.endereco?.logradouro || ''}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.responsavelLegal?.endereco?.logradouro &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.endereco?.logradouro}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <LabelValue label='Número *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.endereco?.numero && !!formEtapa.erros.responsavelLegal?.endereco?.numero}>
                                        <OutlinedInput
                                            name="responsavelLegal.endereco.numero"
                                            value={formEtapa.valores.responsavelLegal?.endereco?.numero || ''}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.responsavelLegal?.endereco?.numero &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.endereco?.numero}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <LabelValue label='Complemento'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth>
                                        <OutlinedInput
                                            name="responsavelLegal.endereco.complemento"
                                            value={formEtapa.valores.responsavelLegal?.endereco?.complemento || ''}
                                            onChange={formEtapa.aoMudar}
                                        />
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <LabelValue label='Bairro *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.endereco?.bairro && !!formEtapa.erros.responsavelLegal?.endereco?.bairro}>
                                        <OutlinedInput
                                            name="responsavelLegal.endereco.bairro"
                                            value={formEtapa.valores.responsavelLegal?.endereco?.bairro || ''}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.responsavelLegal?.endereco?.bairro &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.endereco?.bairro}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <LabelValue label='Cidade *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.endereco?.cidade?.nome && !!formEtapa.erros.responsavelLegal?.endereco?.cidade?.nome}>
                                        <OutlinedInput
                                            name="responsavelLegal.endereco.cidade.nome"
                                            value={formEtapa.valores.responsavelLegal?.endereco?.cidade?.nome || ''}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                        />
                                        {formEtapa.tocados.responsavelLegal?.endereco?.cidade?.nome &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.endereco?.cidade?.nome}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <LabelValue label='UF *'>
                                    <FormControl variant="outlined" disabled={somenteConsulta} fullWidth
                                        error={formEtapa.tocados.responsavelLegal?.endereco?.cidade?.uf?.uf && !!formEtapa.erros.responsavelLegal?.endereco?.cidade?.uf?.uf}>
                                        <OutlinedInput
                                            name="responsavelLegal.endereco.cidade.uf.uf"
                                            value={formEtapa.valores.responsavelLegal?.endereco?.cidade?.uf?.uf || ''}
                                            onChange={formEtapa.aoMudar}
                                            onBlur={formEtapa.aoSair}
                                            placeholder="SP"
                                        />
                                        {formEtapa.tocados.responsavelLegal?.endereco?.cidade?.uf?.uf &&
                                            <FormHelperText>{formEtapa.erros.responsavelLegal?.endereco?.cidade?.uf?.uf}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                        </>
                    )}
                </Grid>
            )
        }
    ];

    const tabContent = buildTabContent(sections, tabEntries, formEtapa);

    return (
        <>
            <form noValidate autoComplete="off">
                <GenericTab
                    ref={tabsRef}
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                    scrollButtons="auto"
                    noAppBar
                    mobileVariant="accordion"
                    className={classes.tabs}
                    activeId={activeSectionId}
                    content={tabContent}
                />
            </form>
            <DialogBuscarEndereco
                open={buscarEnderecoOpen}
                onClose={() => setBuscarEnderecoOpen(false)}
                onSelect={(selectedCep) => {
                    const mappedCidade = selectedCep.cidade || {
                        nome: selectedCep.cidadeNome || '',
                        uf: { uf: '' }
                    };
                    if (addressTargetType === 'candidato') {
                        formEtapa.definirValor('endereco.cep', selectedCep.cep || '');
                        formEtapa.definirValor('endereco.logradouro', selectedCep.logradouro || '');
                        formEtapa.definirValor('endereco.bairro', selectedCep.bairro || '');
                        formEtapa.definirValor('endereco.cidade', mappedCidade);
                    } else {
                        formEtapa.definirValor('responsavelLegal.endereco.cep', selectedCep.cep || '');
                        formEtapa.definirValor('responsavelLegal.endereco.logradouro', selectedCep.logradouro || '');
                        formEtapa.definirValor('responsavelLegal.endereco.bairro', selectedCep.bairro || '');
                        formEtapa.definirValor('responsavelLegal.endereco.cidade', mappedCidade);
                    }
                }}
            />
        </>
    );
});

FormDadosPessoais.displayName = 'FormDadosPessoais';
export default FormDadosPessoais;
